// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    addToChainAfter,
    addToChainBefore,
    executeAwaitableHandlerChain,
    Handler,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { recentlyViewedGetProductCollectionParameter } from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProductByPath from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    LoadProductIfNeeded,
    RequestVariantChildrenFromApi,
    SetupVariantSelection as VendorSetupVariantSelection,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import {
    chain as pricingAndInventoryChain,
    LoadRealTimeInventory,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/LoadProductPricingAndInventory";
import { getProductByPath } from "@keller/App/Services/ProductServiceV2";

type HandlerType = Handler<
    { path: string; styledOption?: string },
    { product?: ProductModel; variantChildren?: ProductModel[]; variantSelection?: SafeDictionary<string> }
>;

const LoadProductWithAdditionalExpands: typeof LoadProductIfNeeded = props => {
    (props.parameter as { additionalExpands?: string[] }).additionalExpands = [
        ...((props.parameter as { additionalExpands?: string[] }).additionalExpands || []),
        "getBadges",
        "properties",
    ];
};

const XngLoadProductIfNeeded: typeof LoadProductIfNeeded = async props => {
    // if variantTypeId is not null this has variantTraits, and we need those loaded fully for this handler chain to work correctly
    // if product is discontinued then it may have replacement product, and we wait for it to be loaded
    if (!props.product || props.product.variantTypeId || props.product.isDiscontinued) {
        props.product = await executeAwaitableHandlerChain(
            loadProductByPath,
            { ...props.parameter, addToRecentlyViewed: true },
            props,
        );
    } else if (
        !props.product.detail ||
        !props.product.content ||
        !props.product.images ||
        !props.product.documents ||
        !props.product.specifications ||
        !props.product.attributeTypes ||
        !props.product.variantTraits
    ) {
        props.dispatch(
            loadProductByPath({
                ...props.parameter,
                addToRecentlyViewed: true,
                onComplete: productProps => {
                    const configuration = productProps?.apiResult?.detail?.configuration;
                    if (configuration) {
                        props.dispatch({
                            type: "Pages/ProductDetails/InitConfigurationSelection",
                            configuration,
                        });
                    }

                    props.dispatch({
                        type: "Data/Products/ReplaceDataView",
                        parameter: recentlyViewedGetProductCollectionParameter,
                        dataView: {},
                    });
                },
            }),
        );
    } else {
        // XNG-Changes: for calling overridden getProductByPath
        getProductByPath({ ...props.parameter, addToRecentlyViewed: true, expand: ["attributes"] }).then(result => {
            props.dispatch({
                type: "Data/Products/CompleteLoadProduct",
                model: result,
            });
        });
    }
};

/**
 * Ensures the PDP URL always reflects a valid variant option so a shopper never lands on a
 * variant product with no trait selected (e.g. from an old or shared link).
 *
 * Case A - a variant CHILD URL was opened directly: hard-redirect to the parent product page
 *          with the `?option=` query parameter (the child's canonicalUrl).
 * Case B - a variant PARENT was opened without an `?option=` parameter (e.g. /Product/kol-20000):
 *          rewrite the URL to include the auto-selected/first variant option
 *          (/Product/kol-20000?option=928604).
 */
const RedirectVariantChildToParent: HandlerType = props => {
    if (typeof window === "undefined" || !props.product || props.parameter.styledOption) {
        return;
    }

    // Case A: variant child opened directly -> redirect to parent product page with ?option=
    if (
        !props.product.isVariantParent &&
        props.product.canonicalUrl &&
        props.product.canonicalUrl.includes("?option=")
    ) {
        // Redirect to the canonical URL (parent product with ?option= parameter)
        window.location.href = props.product.canonicalUrl;
        return false; // Stop the handler chain
    }

    // Case B: variant parent opened without an option -> reflect the selected/first option in the URL
    if (
        props.product.isVariantParent &&
        props.variantChildren &&
        props.variantChildren.length > 0 &&
        props.variantSelection &&
        !/[?&]option=/i.test(window.location.href)
    ) {
        const selection = props.variantSelection;
        // Prefer the child matching the current (auto-)selection, otherwise fall back to the first child.
        const selectedChild =
            props.variantChildren.find(
                child =>
                    !!child.childTraitValues?.length &&
                    child.childTraitValues.every(ctv => selection[ctv.styleTraitId] === ctv.id),
            ) ?? props.variantChildren[0];

        if (selectedChild?.productNumber) {
            const url = new URL(window.location.href);
            url.searchParams.set("option", selectedChild.productNumber);
            window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
        }
    }
};

export const XngRequestVariantChildrenFromApi: typeof RequestVariantChildrenFromApi = async props => {
    if (!props.product?.variantTraits || props.product.variantTraits.length === 0) {
        return;
    }
    // XNG-Changes: Always call loadVariantChildren and remove cached variantChildren, as the PLP cache returns incorrect cpn and upcCode.
    props.variantChildren = await executeAwaitableHandlerChain(
        loadVariantChildren,
        { productId: props.product.id },
        props,
    );
};

/**
 * Custom variant selection logic for Keller Supply.
 * Auto-selects the first variant when no URL option, default child, or default traits are set.
 */
const KellerSetupVariantSelection: typeof VendorSetupVariantSelection = props => {
    props.variantSelection = getVariantSelection(props.product, props.variantChildren, props.parameter.styledOption);
};

function getVariantSelection(product?: ProductModel, variantChildren?: ProductModel[], styledOption?: string) {
    const variantSelection: SafeDictionary<string> = {};
    if (!product?.isVariantParent || !variantChildren || variantChildren.length === 0) {
        return variantSelection;
    }

    // Priority 1: URL parameter (?option=xyz)
    if (styledOption) {
        const variantChild = variantChildren.find(o => o.productNumber.toLowerCase() === styledOption.toLowerCase());
        if (variantChild?.childTraitValues) {
            variantChild.childTraitValues.forEach(o => {
                variantSelection[o.styleTraitId] = o.id;
            });
            if (Object.keys(variantSelection).length > 0) {
                return variantSelection;
            }
        }
    }

    // Priority 2: Default child product ID
    if (product.defaultChildProductId) {
        const variantChild = variantChildren.find(o => o.id === product.defaultChildProductId);
        if (variantChild?.childTraitValues) {
            variantChild.childTraitValues.forEach(o => {
                variantSelection[o.styleTraitId] = o.id;
            });
            if (Object.keys(variantSelection).length > 0) {
                return variantSelection;
            }
        }
    }

    // Priority 3: Traits marked as default
    let hasDefaults = false;
    product.variantTraits?.forEach(variantTrait => {
        const defaultVariantValue = variantTrait.traitValues?.find(traitValue => traitValue.isDefault);
        if (defaultVariantValue) {
            variantSelection[variantTrait.id] = defaultVariantValue.id;
            hasDefaults = true;
        } else {
            variantSelection[variantTrait.id] = "";
        }
    });

    if (hasDefaults && Object.values(variantSelection).every(v => v !== "")) {
        return variantSelection;
    }

    // Priority 4: Auto-select first variant child (Keller customization)
    // This ensures the product displays as a group with variant swatches while pre-selecting the first option
    const firstVariant = variantChildren[0];
    if (firstVariant?.childTraitValues && firstVariant.childTraitValues.length > 0) {
        firstVariant.childTraitValues.forEach(o => {
            variantSelection[o.styleTraitId] = o.id;
        });
    }

    return variantSelection;
}

/**
 * Keep a variant CHILD selected after real-time inventory removes out-of-stock variants.
 *
 * The vendor LoadRealTimeInventory handler, when real-time inventory reports a discontinued/out-of-stock
 * variant child, rebuilds the variant list and re-dispatches the selection using the variant PARENT's
 * product info with `variantSelectionCompleted: false`. That reset (an async event that lands a moment
 * after the page paints) has two visible side effects:
 *   1. The PDP switches to the parent context, hiding child-specific brand / product information.
 *   2. The variant options widget then strips the `?option=` parameter from the URL.
 *
 * This override mirrors the vendor logic exactly but preserves a real variant child selection: it keeps
 * the currently selected child when it is still available, otherwise it falls back to the default child
 * or the first available child - never the bare parent. The inventory API call and the inventory results
 * dispatch are untouched.
 */
const KellerLoadRealTimeInventory: typeof LoadRealTimeInventory = props => {
    const {
        productSettings: { showInventoryAvailability },
        websiteSettings: { includeRealTimeInventoryAndPricingForSeo },
    } = getSettingsCollection(props.getState());
    if (!props.parameter.product || (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo)) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: [props.parameter.product.id, ...(props.parameter.variantChildren?.map(o => o.id) ?? [])],
            onComplete: realTimeInventoryProps => {
                const realTimeInventory = realTimeInventoryProps?.apiResult;

                if (realTimeInventoryProps?.error) {
                    if (props.parameter.product) {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimeInventory",
                            productId: props.parameter.product.id,
                        });
                    }
                } else if (realTimeInventory) {
                    if (
                        realTimeInventory.realTimeInventoryResults &&
                        props.parameter.variantChildren &&
                        props.parameter.product
                    ) {
                        const product = props.parameter.product;
                        const discontinued: SafeDictionary<boolean> = {};
                        for (const result of realTimeInventory.realTimeInventoryResults) {
                            if (!result.qtyOnHand && showInventoryAvailability) {
                                discontinued[result.productId] = true;
                            }
                        }
                        const initialCount = props.parameter.variantChildren.length;
                        const newVariantChildren = props.parameter.variantChildren.filter(
                            o => !o.isDiscontinued || (o.isDiscontinued && !discontinued[o.id]),
                        );

                        if (initialCount !== newVariantChildren.length) {
                            const variantChildrenDataView = getVariantChildrenDataView(props.getState(), product.id);
                            const { value, ...otherProps } = variantChildrenDataView;
                            props.dispatch({
                                type: "Data/Products/ReplaceDataView",
                                parameter: {
                                    productId: product.id,
                                    variantChildren: true,
                                },
                                dataView: {
                                    value: newVariantChildren,
                                    ...otherProps,
                                },
                            });

                            // XNG-Changes: preserve a CHILD selection rather than resetting to the parent.
                            const state = props.getState();
                            const currentSelectedId = state.pages.productDetails.selectedProductId;
                            const targetChild =
                                newVariantChildren.find(o => o.id === currentSelectedId) ??
                                newVariantChildren.find(o => o.id === product.defaultChildProductId) ??
                                newVariantChildren[0];

                            const childVariantSelection: SafeDictionary<string> = {};
                            targetChild?.childTraitValues?.forEach(o => {
                                childVariantSelection[o.styleTraitId] = o.id;
                            });
                            const hasChildSelection = Object.keys(childVariantSelection).length > 0;

                            let selectedProductInfo: ProductInfo;
                            if (hasChildSelection && targetChild) {
                                selectedProductInfo =
                                    state.pages.productDetails.productInfosById?.[targetChild.id] ??
                                    createFromProduct(getProductState(state, targetChild.id).value ?? targetChild);
                            } else {
                                selectedProductInfo =
                                    state.pages.productDetails.productInfosById?.[product.id] ??
                                    createFromProduct(getProductState(state, product.id).value ?? product);
                            }

                            props.dispatch({
                                type: "Pages/ProductDetails/UpdateVariantSelection",
                                variantSelection: childVariantSelection,
                                variantSelectionCompleted: hasChildSelection,
                                selectedProductInfo,
                            });
                        }
                    }

                    props.dispatch({
                        type: "Pages/ProductDetails/CompleteLoadRealTimeInventory",
                        realTimeInventory,
                    });
                }

                props.inventoryLoaded = true;
            },
        }),
    );
};

addToChainBefore(chain, LoadProductIfNeeded, LoadProductWithAdditionalExpands);
replaceInChain(chain, LoadProductIfNeeded, XngLoadProductIfNeeded);
replaceInChain(chain, RequestVariantChildrenFromApi, XngRequestVariantChildrenFromApi);
replaceInChain(chain, VendorSetupVariantSelection, KellerSetupVariantSelection);
// Run the redirect after variant children and selection are available so the parent-without-option
// case (Case B) can resolve the option to add to the URL.
addToChainAfter(chain, KellerSetupVariantSelection, RedirectVariantChildToParent);
// Keep a variant child selected after real-time inventory removes out-of-stock variants (prevents the
// PDP from reverting to the parent context and the URL from losing its ?option= parameter).
replaceInChain(pricingAndInventoryChain, LoadRealTimeInventory, KellerLoadRealTimeInventory);